/**
 * Full sign-out for shared-computer safety: destroy Aegis session, clear local/OIDC state,
 * and redirect through Keycloak end_session so SSO does not re-bind the previous user.
 */
import type { UserManager } from 'oidc-client-ts'
import { SessionsApi } from '@/lib/generated-api/apis/SessionsApi'
import { createCsrfEnabledConfig } from '@/lib/csrf-enabled-client'
import { clearSessionData } from '@/lib/session-management'
import { hardRedirectToEndSession } from '@/lib/oidc-end-session'
import {
  type SessionCleanupReason,
  SESSION_CLEANUP_MESSAGES,
} from '@/lib/utils'
import { readStoredAccessToken } from '@/lib/auth-token-storage'
import { wipeAppAuthState } from '@/lib/wipe-app-auth-state'
import { clearLoginDomainConfigFromSessionStorage } from '@/lib/login-domain'
import { isSecurityDashboardPdfPrintBrowserContext } from '@/lib/security-dashboard-pdf-injected-snapshot'
import { getApiContextPath, normalizeApiBaseUrl } from '@/lib/utils/url-normalization'
import { logger } from '@/lib/logger'

export type ForceFullSignOutOptions = {
  reason: SessionCleanupReason
  userManager?: UserManager | null
  sessionId?: string
  authority?: string
  clientId?: string
  idTokenHint?: string
  postLogoutRedirectUri?: string
  /** Invoked after backend destroy, before storage wipe (e.g. reset AuthProvider React state). */
  onBeforeStorageClear?: () => void
  /** Invoked after storage wipe, before IdP redirect (e.g. disable UserManager in React). */
  onAfterStorageClear?: () => void
  /** When true, skip Keycloak redirect (unit tests). */
  skipIdpRedirect?: boolean
  /** When false, do not set sessionMessage on the login page. */
  setSessionMessage?: boolean
  /** Delay after backend destroy (ms); use 0 in unit tests. */
  destroyDelayMs?: number
  /** After soft sign-out (skipIdpRedirect), navigate here. Default '/'. */
  softSignOutRedirectTo?: string
}

let signOutInProgress = false

function readSessionIdFromStorage(): string | undefined {
  try {
    const raw = localStorage.getItem('session')
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as { sessionId?: string }
    return parsed.sessionId
  } catch {
    return undefined
  }
}

function readIdTokenHintFromStorage(): string | undefined {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('oidc.user:')) {
        const raw = localStorage.getItem(key)
        if (!raw) continue
        const parsed = JSON.parse(raw) as { id_token?: string }
        if (parsed.id_token) return parsed.id_token
      }
    }
  } catch {
    /* ignore */
  }
  return undefined
}

async function destroyBackendSession(sessionId: string): Promise<void> {
  const accessToken = readStoredAccessToken()
  const envBase = process.env.NEXT_PUBLIC_API_BASE_URL as string
  const contextPath = getApiContextPath()
  const { apiBase, rootBase } = normalizeApiBaseUrl(envBase, { contextPath })

  if (accessToken) {
    const config = createCsrfEnabledConfig(accessToken, rootBase)
    const sessionsApi = new SessionsApi(config)
    try {
      await sessionsApi.destroySession(
        { sessionId },
        { credentials: 'include' }
      )
    } catch (e) {
      const status =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined
      if (status !== 404 && status !== 403) {
        logger.warn('[forceFullSignOut] Backend session destroy failed:', e)
      }
    }
    return
  }

  if (!sessionId) return

  try {
    await fetch(`${apiBase}/v1/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
  } catch (e) {
    logger.warn('[forceFullSignOut] Cookie-only session destroy failed:', e)
  }
}

/**
 * Idempotent full sign-out. Safe to call from sessionInvalid, timeouts, and explicit logout.
 */
export async function forceFullSignOut(options: ForceFullSignOutOptions): Promise<void> {
  if (typeof window === 'undefined') return
  if (isSecurityDashboardPdfPrintBrowserContext()) {
    logger.debug(`[forceFullSignOut] Skipped (PDF print) — reason: ${options.reason}`)
    return
  }
  if (signOutInProgress) return
  signOutInProgress = true

  const {
    reason,
    userManager,
    onBeforeStorageClear,
    onAfterStorageClear,
    skipIdpRedirect = false,
    setSessionMessage = true,
    softSignOutRedirectTo = '/',
  } = options

  const sessionMessage = setSessionMessage ? SESSION_CLEANUP_MESSAGES[reason] : null

  const sessionId = options.sessionId ?? readSessionIdFromStorage()
  const authority =
    options.authority ??
    process.env.NEXT_PUBLIC_OIDC_AUTHORITY ??
    userManager?.settings?.authority ??
    ''
  const clientId =
    options.clientId ??
    process.env.NEXT_PUBLIC_OIDC_CLIENT_ID ??
    userManager?.settings?.client_id ??
    ''
  const idTokenHint = options.idTokenHint ?? readIdTokenHintFromStorage()
  const postLogoutRedirectUri =
    options.postLogoutRedirectUri ?? `${window.location.origin}/auth/logout`

  try {
    if (userManager) {
      try {
        userManager.stopSilentRenew()
      } catch {
        /* ignore */
      }
    }

    if (sessionId) {
      await destroyBackendSession(sessionId)
      const delayMs = options.destroyDelayMs ?? 200
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
      }
    }

    onBeforeStorageClear?.()
    clearLoginDomainConfigFromSessionStorage()
    try {
      sessionStorage.removeItem('oidc_config_for_callback')
    } catch {
      /* ignore */
    }
    clearSessionData()

    if (userManager) {
      try {
        await userManager.removeUser().catch(() => undefined)
        await userManager.clearStaleState().catch(() => undefined)
      } catch {
        /* ignore */
      }
    }

    await wipeAppAuthState({
      preserveTheme: true,
      sessionMessage,
      setLogoutFlags: true,
    })

    onAfterStorageClear?.()

    signOutInProgress = false

    if (skipIdpRedirect) {
      window.location.replace(softSignOutRedirectTo)
      return
    }

    await hardRedirectToEndSession({
      authority: authority || undefined,
      clientId: clientId || undefined,
      idTokenHint,
      postLogoutRedirectUri,
    })
  } catch (err) {
    logger.error('[forceFullSignOut] Error during sign-out:', err)
    signOutInProgress = false
    if (!skipIdpRedirect) {
      window.location.href = '/'
    }
    throw err
  }
}

/** Reset guard after tests. */
export function resetForceFullSignOutGuardForTests(): void {
  signOutInProgress = false
}
